/**
 * Order Management — page entry: wires layout sections and order data hook.
 */

import React, { useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import moment from 'moment-timezone';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';

import {
  COLORS,
  COLORS_NEXT_DAY_PROMO,
  ORDER_TYPE_CLOUD_KITCHEN,
  ORDER_TYPE_NEXT_DAY_PROMOTION,
  ORDER_TYPE_RESTAURANT,
} from './orderManagementConfig';
import { useOrderManagementOrders } from './useOrderManagementOrders';
import { OrderManagementPageHeader } from './OrderManagementPageHeader';
import { OrderManagementTypeToggle } from './OrderManagementTypeToggle';
import { OrderManagementComingSoonPlaceholder } from './OrderManagementComingSoonPlaceholder';
import { OrderManagementCloudKitchenPanel } from './OrderManagementCloudKitchenPanel';
import { buildOrderMapNavigationState } from './useOrderMapPageOrders';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import { localStorageKeys } from '../../utils/constants';

const formattedDateTime = () => {
  return moment().format('MM/DD/YYYY - hh:mm A');
};

const OrderManagement = () => {
  const socketRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const {
    selectedMenuTab,
    setSelectedMenuTab,
    onLoadRegBranchResponse,
    orderFromSocket,
    setOrderFromSocket,
    branchSessionLogout,
    setStatsDataFromSocket,
  } = useContext(GlobalStateDataContext);
  const { TOKEN } = localStorageKeys;
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [notificationServerDown, setNotificationServerDown] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const URL_PATH_NOTIFICATION_SERVER = process.env.REACT_APP_NOTIFICATION_SERVER_URL; // WE HAVE TO REPLACE BEFORE GOING GIVE
  const RegionalBranchName =
    onLoadRegBranchResponse?.data?.data?.data?.RegionalBranchInfo?.branchName;
  const navigate = useNavigate();
  const orders = useOrderManagementOrders();

  const handleViewOrderMap = () => {
    navigate('/ordermanagement/map', {
      state: buildOrderMapNavigationState(orders),
    });
  };

  const isNextDayPromo = orders.orderTypeView === ORDER_TYPE_NEXT_DAY_PROMOTION;
  const activeColors = isNextDayPromo ? COLORS_NEXT_DAY_PROMO : COLORS;

  useEffect(() => {
    const RELOAD_LOCK_KEY = 'regional_socket_reloaded_once';
    let reloadTimer = null;

    const socket = io(URL_PATH_NOTIFICATION_SERVER, {
      reconnection: true,
      reconnectionAttempts: Infinity,
      forceNew: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 2000,
      timeout: 5000,
      query: {
        type: 'regional_frontend_app',
      },
      transports: ['websocket'],
      auth: {
        token: localStorage.getItem(TOKEN),
      },
    });

    socketRef.current = socket;

    const reloadOnceAfterDisconnect = () => {
      const alreadyReloaded = sessionStorage.getItem(RELOAD_LOCK_KEY);

      if (alreadyReloaded) return;

      sessionStorage.setItem(RELOAD_LOCK_KEY, 'true');

      reloadTimer = setTimeout(() => {
        window.location.reload();
      }, 1000);
    };

    const isPromoOrderNo = (orderNo) =>
      typeof orderNo === 'string' && orderNo.toUpperCase().includes('-PROMO');

    const handleRegionalStats = (arg, callback) => {
      try {
        // Open only for debugging
        console.log(`📶 Order from Notification Server ${formattedDateTime()}`, arg);

        setLastUpdated(formattedDateTime());

        if (arg?.order_no) {
          setOrderFromSocket((prev) => {
            const index = prev.findIndex((order) => order.order_no === arg.order_no);

            if (index !== -1) {
              const updated = [...prev];
              updated[index] = arg;
              return updated;
            }

            return [...prev, arg];
          });
        }

        if (arg?.order_no || arg?.id || arg?._id) {
          const socketId = arg?.id ?? arg?._id;
          orders.setAllOrders((prev) => {
            // Ignore promo socket events in restaurant data sets.
            // Promo updates are still allowed when current list is promo-based.
            if (isPromoOrderNo(arg?.order_no)) {
              const hasPromoOrders = prev.some((order) => isPromoOrderNo(order?.order_no));
              if (!hasPromoOrders) return prev;
            }

            const index = prev.findIndex(
              (order) =>
                (arg?.order_no && order.order_no === arg.order_no) ||
                (socketId && (order._id === socketId || order.id === socketId))
            );

            if (index === -1) return [...prev, arg];

            const updated = [...prev];
            updated[index] = { ...updated[index], ...arg };
            return updated;
          });
        }

        if (typeof callback === 'function') {
          callback({
            received: true,
            regionalBranchName: RegionalBranchName,
            receivedAt: new Date().toISOString(),
            message: 'Regional frontend received stats successfully',
          });
        }
      } catch (error) {
        console.error('Error processing regional stats:', error);

        if (typeof callback === 'function') {
          callback({
            received: false,
            regionalBranchName: RegionalBranchName,
            error: error.message,
            receivedAt: new Date().toISOString(),
            message: 'Regional frontend failed to process stats',
          });
        }
      }
    };

    socket.on('connect', () => {
      reconnectAttemptsRef.current = 0;
      setConnectionStatus('Connected');
      setNotificationServerDown(false);
      console.log(`Connected ${socket.id} ${formattedDateTime()}`);
    });

    socket.on('disconnect', (reason) => {
      setConnectionStatus('Disconnected: ' + reason);
      setNotificationServerDown(true);

      console.log(`Disconnected ${reason} ${formattedDateTime()}`);

      // Reload only after real disconnect, once
      reloadOnceAfterDisconnect();
    });

    socket.on('reconnect_attempt', (attemptNumber) => {
      reconnectAttemptsRef.current = attemptNumber;
      setConnectionStatus(`Reconnecting... Attempt ${attemptNumber}`);
      console.log(`Reconnection attempt #${attemptNumber}`);
    });

    socket.on('reconnect_failed', () => {
      setConnectionStatus('Reconnection failed');
      setNotificationServerDown(true);
      console.error('Notification server failed to reconnect');
    });

    socket.on('reconnect', () => {
      setNotificationServerDown(false);
      setConnectionStatus('Reconnected successfully');
      console.log('Notification server reconnected successfully');
    });

    socket.on('connect_error', (e) => {
      console.log(`Socket Server is down ${e.message} ${formattedDateTime()}`);
      setNotificationServerDown(true);
    });

    socket.on('socket-auth-error', (data) => {
      //   console.log('Socket auth error:', data);
      if (data?.message === 'Invalid token') {
        branchSessionLogout(navigate);
        setNotificationServerDown(true);
      }
    });

    const pingInterval = setInterval(() => {
      if (socket.connected) {
        socket.emit('ping-from-regionalbranch', {}, (ack) => {
          // Below console is to Testing what data coming from server as stats
          // console.log('Regional ping ACK:', ack);
          setStatsDataFromSocket(ack?.stats);
        });

        console.log(`📡 Sent ping from OrdersManagement ${formattedDateTime()}`);
      }
    }, 60000);

    socket.on(`pong-${RegionalBranchName}`, handleRegionalStats);
    socket.on(RegionalBranchName, handleRegionalStats);

    return () => {
      if (reloadTimer) clearTimeout(reloadTimer);
      clearInterval(pingInterval);

      socket.off(`pong-${RegionalBranchName}`, handleRegionalStats);
      socket.off(RegionalBranchName, handleRegionalStats);
      socket.off('socket-auth-error');

      socket.disconnect();
    };
  }, [RegionalBranchName]);

  // console.log('---orderFromSocket', orderFromSocket);
  // console.log('---orders', orders);

  const tableProps = {
    loading: orders.loading,
    loadError: orders.loadError,
    filteredOrders: orders.filteredOrders,
    displayedRows: orders.displayedRows,
    expandedId: orders.expandedId,
    toggleRow: orders.toggleRow,
    displayPage: orders.displayPage,
    setDisplayPage: orders.setDisplayPage,
    rowsPerPage: orders.rowsPerPage,
    setRowsPerPage: orders.setRowsPerPage,
  };

  return (
    <Box
      sx={{ width: '100%', minHeight: '100vh', bgcolor: activeColors.pageBg, py: { xs: 3, sm: 4 } }}
    >
      <OrderManagementPageHeader
        filteredCount={orders.filteredOrders.length}
        totalCount={orders.totalCount}
        deliveryDate={orders.deliveryDate}
        onDeliveryDateChange={orders.setDeliveryDate}
        hideDeliveryDatePicker={orders.isNextDayPromo}
        fixedDateLabel={orders.nextDayPromoDateLabel}
        loading={orders.loading}
        lastRefreshLabel={orders.formatLastRefresh(orders.lastRefreshAt)}
        onRefresh={orders.refresh}
        colors={activeColors}
      />

      <OrderManagementTypeToggle
        value={orders.orderTypeView}
        onChange={orders.setOrderTypeView}
        onViewMap={handleViewOrderMap}
        colors={activeColors}
      />

      <Box
        sx={{
          maxWidth: 1320,
          mx: 'auto',
          bgcolor: activeColors.white,
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}
      >
        {orders.orderTypeView === ORDER_TYPE_RESTAURANT && (
          <OrderManagementCloudKitchenPanel
            statusFilter={orders.statusFilter}
            onStatusFilterChange={orders.setStatusFilter}
            tabCounts={orders.tabCounts}
            orderIdSearch={orders.orderIdSearch}
            onOrderIdSearchChange={orders.setOrderIdSearch}
            deliveryTypeFilter={orders.deliveryTypeFilter}
            onDeliveryTypeFilterChange={orders.setDeliveryTypeFilter}
            loadError={orders.loadError}
            tableProps={tableProps}
            colors={COLORS}
          />
        )}

        {orders.orderTypeView === ORDER_TYPE_NEXT_DAY_PROMOTION && (
          <OrderManagementCloudKitchenPanel
            variant='nextDayPromo'
            statusFilter={orders.statusFilter}
            onStatusFilterChange={orders.setStatusFilter}
            nextDayPromoDayFilter={orders.nextDayPromoDayFilter}
            onNextDayPromoDayFilterChange={orders.setNextDayPromoDayFilter}
            nextDayPromoTabCounts={orders.nextDayPromoTabCounts}
            orderIdSearch={orders.orderIdSearch}
            onOrderIdSearchChange={orders.setOrderIdSearch}
            deliveryTypeFilter={orders.deliveryTypeFilter}
            onDeliveryTypeFilterChange={orders.setDeliveryTypeFilter}
            loadError={orders.loadError}
            tableProps={tableProps}
            colors={COLORS_NEXT_DAY_PROMO}
          />
        )}

        {orders.orderTypeView === ORDER_TYPE_CLOUD_KITCHEN && (
          <OrderManagementComingSoonPlaceholder
            title='Cloud Kitchen'
            description='Cloud kitchen order management is coming soon. Switch to Restaurant to view active orders.'
            Icon={CloudOutlinedIcon}
          />
        )}
      </Box>
    </Box>
  );
};

export default OrderManagement;
